/**
 * Notify me (sold out), modeled on a SwiftUI "Notify Me" button:
 * - the bell rings on hover and once when the button scrolls into view;
 * - tapping it stretches the pill into an inline email bar: the bell blurs away,
 *   the label glides to the right into its own white pill (now the submit button),
 *   and the "Email" field blurs in on the left;
 * - tapping elsewhere with the field empty, or Escape, folds it back.
 * The email field and button submit the back-in-stock customer form (form="back-in-stock-form").
 * Transform, opacity and a small blur only. Reduced motion: instant.
 */
const SPRING = 'cubic-bezier(0.32, 1.3, 0.4, 1)';
const SOFT = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ring = (button) => {
  const bell = button?.querySelector('.restock-bell');
  if (!bell || reduce() || bell.getAnimations().length || button.closest('[data-open]')) return;
  bell.animate(
    [
      { transform: 'rotate(0deg)' }, { transform: 'rotate(18deg)', offset: 0.12 }, { transform: 'rotate(-15deg)', offset: 0.28 },
      { transform: 'rotate(11deg)', offset: 0.44 }, { transform: 'rotate(-7deg)', offset: 0.6 }, { transform: 'rotate(3deg)', offset: 0.76 },
      { transform: 'rotate(0deg)' },
    ],
    { duration: 800, easing: 'ease-out' }
  );
};

const morph = (wrap, open) => {
  const button = wrap.querySelector('[data-restock-button]');
  const label = wrap.querySelector('[data-restock-label]');
  const email = wrap.querySelector('[data-restock-email]');
  const bell = wrap.querySelector('.restock-bell');
  const pill = wrap.querySelector('.restock-inline__pill');
  if (!button || !label || !email) return;
  if (!button.dataset.closedLabel) button.dataset.closedLabel = label.textContent;

  const labelFrom = label.getBoundingClientRect();
  const bellFrom = bell?.getBoundingClientRect();
  const wrapBox = wrap.getBoundingClientRect();

  wrap.toggleAttribute('data-open', open);
  button.setAttribute('aria-expanded', String(open));
  label.textContent = open ? (button.dataset.submitLabel || label.textContent) : button.dataset.closedLabel;
  email.tabIndex = open ? 0 : -1;
  if (open) {
    button.type = 'submit';
    if (wrap.dataset.formId) button.setAttribute('form', wrap.dataset.formId);
  } else {
    button.type = 'button';
    button.removeAttribute('form');
  }

  if (reduce()) { if (open) email.focus({ preventScroll: true }); return; }

  // The label glides from where it was to its new spot (FLIP, transform only).
  const labelTo = label.getBoundingClientRect();
  const dx = labelFrom.left + labelFrom.width / 2 - (labelTo.left + labelTo.width / 2);
  label.animate(
    [{ transform: `translateX(${dx}px)`, filter: 'blur(0px)' }, { transform: `translateX(${dx * 0.3}px)`, filter: 'blur(2px)', offset: 0.35 }, { transform: 'none', filter: 'blur(0px)' }],
    { duration: 620, easing: SPRING }
  );

  // White pill grows in behind the label (out when folding back).
  pill?.getAnimations().forEach((a) => a.cancel());
  pill?.animate(
    open
      ? [{ opacity: 0, transform: `translateX(${dx * 0.6}px) scale(0.7, 0.8)` }, { opacity: 1, transform: 'none' }]
      : [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(0.7, 0.8)' }],
    { duration: 560, easing: SPRING, fill: 'both' }
  );

  // Bell blurs away where it stood (or blurs back in).
  if (bell && bellFrom) {
    if (open) {
      const ghost = bell.cloneNode(true);
      ghost.classList.add('restock-bell--ghost');
      ghost.style.left = `${bellFrom.left - wrapBox.left}px`;
      ghost.style.top = `${bellFrom.top - wrapBox.top}px`;
      ghost.style.width = `${bellFrom.width}px`;
      ghost.style.height = `${bellFrom.height}px`;
      wrap.append(ghost);
      ghost.animate(
        [{ opacity: 1, filter: 'blur(0px)', transform: 'none' }, { opacity: 0, filter: 'blur(6px)', transform: 'translateX(24px) scale(0.7)' }],
        { duration: 380, easing: SOFT, fill: 'forwards' }
      ).finished.then(() => ghost.remove(), () => ghost.remove());
    } else {
      bell.animate([{ opacity: 0, filter: 'blur(6px)' }, { opacity: 1, filter: 'blur(0px)' }], { duration: 380, delay: 120, easing: SOFT, fill: 'backwards' });
    }
  }

  // Email field blurs in from the left.
  email.animate(
    open
      ? [{ opacity: 0, filter: 'blur(8px)', transform: 'translateX(-10px)' }, { opacity: 1, filter: 'blur(0px)', transform: 'none' }]
      : [{ opacity: 1, filter: 'blur(0px)' }, { opacity: 0, filter: 'blur(8px)' }],
    { duration: open ? 460 : 240, delay: open ? 140 : 0, easing: SOFT, fill: 'backwards' }
  );
  if (open) setTimeout(() => email.focus({ preventScroll: true }), 160);
};

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-restock-button]') : null;
  if (!button) return;
  const wrap = button.closest('[data-restock-inline]');
  if (!wrap || wrap.hasAttribute('data-open')) return; // open: the click submits the form
  event.preventDefault();
  if (wrap.getBoundingClientRect().top < 0 || wrap.getBoundingClientRect().bottom > window.innerHeight) {
    wrap.scrollIntoView({ block: 'center', behavior: reduce() ? 'auto' : 'smooth' });
  }
  morph(wrap, true);
});

document.addEventListener('pointerdown', (event) => {
  document.querySelectorAll('[data-restock-inline][data-open]').forEach((wrap) => {
    const email = wrap.querySelector('[data-restock-email]');
    if (!wrap.contains(event.target) && email && !email.value.trim()) morph(wrap, false);
  });
}, true);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const wrap = event.target instanceof Element ? event.target.closest('[data-restock-inline][data-open]') : null;
  if (wrap) { morph(wrap, false); wrap.querySelector('[data-restock-button]')?.focus(); }
});

document.addEventListener('pointerover', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-restock-button]') : null;
  if (button && event.pointerType === 'mouse') ring(button);
});

// Ring once when a Notify me button first scrolls into view (also after the variant changes).
const seen = new WeakSet();
const io = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => entries.forEach((entry) => {
    if (entry.isIntersecting && !seen.has(entry.target)) { seen.add(entry.target); setTimeout(() => ring(entry.target), 350); }
  }), { threshold: 0.8 })
  : null;
const watch = () => document.querySelectorAll('[data-restock-button]').forEach((b) => { if (!seen.has(b)) io?.observe(b); });
watch();
new MutationObserver(watch).observe(document.body, { childList: true, subtree: true });

// Safety net: make sure the back-in-stock customer form carries the id the inline field and button point to.
const linkForm = () => {
  const form = document.getElementById('back-in-stock-dialog')?.closest('form');
  if (form && !form.id) form.id = 'back-in-stock-form';
};
linkForm();
